/**
 * Блок «Национальный состав» (FE-8, redesign по макету): donut-диаграмма
 * (shadcn chart поверх recharts) с итогом в центре и легендой
 * «% · национальность» под ней.
 *
 * Источник данных — только `PublicGeneralResponse.nationality.groups` из
 * `/api/public/general` (BE-7): полный состав по этносам, доли считаются
 * на фронте; нет данных → placeholder. Демо-моков нет (замечание ревью
 * от 24.08.2026: вымышленные проценты на стендах).
 *
 * Показываются топ-7 этносов по доле, остальные схлопываются в «Прочие»
 * (замечание ревью от 11.08.2026).
 */
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../../components/ui/chart';
import type { PopulationNationality } from '../../../../types/public';

interface Props {
  /** Данные из /public/general; null → placeholder «нет данных». */
  nationality: PopulationNationality | null;
}

/** Палитра макета по известным ключам этносов; вне неё — запасной цикл. */
const KEY_COLORS: Record<string, string> = {
  kazakhs: '#7B9FF2',
  russians: '#3D6BE4',
  germans: '#2B4FC7',
  ukrainians: '#1D3CA9',
  tatars: '#152C85',
  belarusians: '#0D1F60',
  poles: '#5A8DF5',
  ingush: '#8FB3F7',
  armenians: '#BCD0FA',
  mordva: '#4A7BF0',
};
const FALLBACK_COLORS = ['#5A8DF5', '#8FB3F7', '#BCD0FA', '#4A7BF0'];
const OTHERS_COLOR = '#B01BEB';

interface Segment {
  key: string;
  label: string;
  percent: number;
  color: string;
}

/** Сколько этносов показывается отдельно; остальные — в «Прочие». */
const TOP_LIMIT = 7;

/**
 * Топ-N сегментов по доле + агрегат «Прочие» из хвоста. Существующий
 * сегмент «прочие» из данных вливается в агрегат, а не конкурирует
 * с этносами за место в топе.
 */
const topWithOthers = (
  segments: Segment[],
  othersLabel: string,
): Segment[] => {
  const ethnic = segments.filter((s) => s.key !== 'others');
  const othersFromData = segments.filter((s) => s.key === 'others');
  const sorted = [...ethnic].sort((a, b) => b.percent - a.percent);
  const tail = [...sorted.slice(TOP_LIMIT), ...othersFromData];
  const top = sorted.slice(0, TOP_LIMIT);
  if (tail.length === 0) return top;
  return [
    ...top,
    {
      key: 'others',
      label: othersLabel,
      percent: tail.reduce((sum, s) => sum + s.percent, 0),
      color: OTHERS_COLOR,
    },
  ];
};

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmtPct = (v: number): string =>
  `${Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)}%`;

export const PopulationNationalityBlock = ({ nationality }: Props) => {
  const { t } = useTranslation();

  const rows = Object.entries(nationality?.groups ?? {});
  const total = rows.reduce((sum, [, value]) => sum + value, 0);
  let segments: Segment[] = rows.map(([key, value], index) => ({
    key,
    // Подпись — из словаря i18n; незнакомый ключ бэка выводится как есть.
    label: t(`digitalPassport.populationNationality.${key}`, {
      defaultValue: key,
    }),
    percent: total === 0 ? 0 : (value / total) * 100,
    color:
      key === 'others'
        ? OTHERS_COLOR
        : (KEY_COLORS[key] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length]),
  }));

  segments = topWithOthers(
    segments,
    t('digitalPassport.populationNationality.others'),
  );

  const isEmpty = segments.length === 0;

  const chartConfig = Object.fromEntries(
    segments.map((s) => [s.key, { label: s.label, color: s.color }]),
  ) satisfies ChartConfig;

  const chartData = segments.map((s) => ({ key: s.key, value: s.percent }));

  return (
    <Card className="h-full shadow-card">
      <CardHeader>
        <CardTitle>
          {t('digitalPassport.populationNationality.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <section data-testid="block-population-nationality">
          {isEmpty ? (
            <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
          ) : (
            <>
              <div className="relative mx-auto w-full max-w-[260px]">
                <ChartContainer config={chartConfig} className="aspect-square">
                  <PieChart>
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent hideLabel />}
                    />
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="key"
                      innerRadius="70%"
                      outerRadius="98%"
                      strokeWidth={0}
                    >
                      {segments.map((s) => (
                        <Cell key={s.key} fill={s.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ChartContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="text-[26px] font-extrabold tabular-nums leading-tight"
                    data-testid="nationality-total"
                  >
                    {fmt(total)}
                  </span>
                  <span className="text-sm text-text-muted">
                    {t('units.people')}
                  </span>
                </div>
              </div>

              {/* Легенда: от 1600px — сетка 4 этноса в ряд (топ-7 +
                  «Прочие» = две строки), уже — обычный перенос по центру. */}
              <ul
                data-testid="nationality-list"
                className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-[15px] min-[1600px]:grid min-[1600px]:grid-cols-4"
              >
                {segments.map((s) => (
                  <li
                    key={s.key}
                    data-testid={`nationality-row-${s.key}`}
                    className="flex items-center gap-2"
                  >
                    <span
                      className="font-bold tabular-nums"
                      // Цвет — из данных (палитра сегментов).
                      style={{ color: s.color }}
                    >
                      {fmtPct(s.percent)}
                    </span>
                    <span
                      className="size-2 shrink-0 rounded-sm"
                      style={{ backgroundColor: s.color }}
                      aria-hidden="true"
                    />
                    <span>{s.label}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
